document.addEventListener("DOMContentLoaded", () => {
    if (typeof customElements.get('pk-store-locator-map') === 'undefined') {
        class PkStoreLocatorMap extends HTMLElement {
            constructor() {
                super();
                this.map = null;
                this.bounds = [];
                this.zoom = parseFloat(this.dataset.zoom) || 13;
                this.clickMarker = null;
                this.markerListDOM = [];

                this.errorMessage = this.dataset.errorMessage;
            }

            connectedCallback() {
                this.initMap();
            }

            async initMap() {
                this.showLoader();

                try {
                    await this.loadMap();

                    this.markerListDOM = Array.from(this.querySelectorAll("[data-marker]"));

                    this.markerListDOM.forEach((item) => {
                        const lat = parseFloat(item.dataset.lat);
                        const lng = parseFloat(item.dataset.lng);

                        if (isNaN(lat) || isNaN(lng)) {
                            console.error(`Invalid coordinates for marker: ${item.dataset}`);
                            return;
                        }

                        const marker = this.addMarker(
                            lat,
                            lng,
                            item.dataset.img,
                            item.dataset.name,
                            item.dataset.address,
                            item.dataset.phone,
                            item.dataset.link
                        );
                        item.addEventListener("click", () => {
                            this.onMarkerClick(item, marker);
                        });
                    });

                    this.centerMap();

                    /* Force resize the map correctly with delay to ensure correct resizing */
                    setTimeout(() => {
                        this.map.invalidateSize();
                    }, 100);
                } catch (error) {
                    this.displayErrorMessage(error.message || this.errorMessage || "An error occurred while loading the map.");
                } finally {
                    this.hideLoader();
                }
            }

            async loadMap() {
                const mapContainer = document.createElement('div');
                mapContainer.classList.add("pk-store-locator__leaflet-container");
                this.appendChild(mapContainer);

                try {
                    this.map = L.map(mapContainer, {
                        scrollWheelZoom: false,
                        maxBounds: [[-90, -180], [90, 180]],
                        maxBoundsViscosity: 1.0
                    });

                    const openStreetMapLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
                        maxZoom: 18,
                        detectRetina: true
                    });

                    openStreetMapLayer.on('tileerror', () => {
                        this.displayErrorMessage(this.errorMessage);
                        mapContainer.style.display = "none";
                    });

                    openStreetMapLayer.addTo(this.map);
                    this.map.attributionControl.setPrefix('<a href="https://leafletjs.com/" target="_blank">Leaflet</a>');
                } catch (error) {
                    console.error('Error loading map:', error);
                    this.displayErrorMessage('An error occurred while loading the map.');
                }
            }

            addMarker(lat, lng, img, name, address, phone, link) {
                const point = [lat, lng];
                this.bounds.push(point);
                return new PkStoreLocatorMarker(this.map, point, img, name, address, phone, link);
            }

            centerMap() {
                if (this.bounds.length > 0) {
                    this.map.fitBounds(this.bounds);
                    this.map.setZoom(this.zoom);
                }
            }

            setView(lat, lng) {
                const point = [lat, lng];
                this.map.setView(point, this.zoom);
            }

            onMarkerClick(item, marker) {
                this.markerListDOM.forEach((item2) => {
                    item2.classList.remove("pk-store-locator__store--active");
                });
                item.classList.add("pk-store-locator__store--active");

                if (this.clickMarker !== null) this.clickMarker.unsetActive();

                marker.setActive();
                this.setView(item.dataset.lat, item.dataset.lng);
                this.clickMarker = marker;
            }

            showLoader() {
                if (!this.querySelector('.pk-store-locator__loader-container')) {
                    const loaderContainer = document.createElement('div');
                    loaderContainer.className = 'pk-store-locator__loader-container';

                    const loader = document.createElement('div');
                    loader.className = 'pk-store-locator__loader';

                    loaderContainer.appendChild(loader);
                    this.appendChild(loaderContainer);
                }
            }

            hideLoader() {
                const loaderContainer = this.querySelector('.pk-store-locator__loader-container');
                if (loaderContainer) {
                    loaderContainer.remove();
                }
            }

            displayErrorMessage(message) {
                const existingError = this.querySelector('.pk-store-locator__error-message');
                if (existingError) existingError.remove();

                const errorMessage = document.createElement('div');
                errorMessage.className = 'pk-store-locator__error-message';
                errorMessage.textContent = message;
                this.appendChild(errorMessage);

                const mapContainer = this.querySelector('.pk-store-locator__leaflet-container');
                if (mapContainer) {
                    mapContainer.style.display = 'none';
                }
            }
        }

        customElements.define('pk-store-locator-map', PkStoreLocatorMap);
    }

    if (typeof PkStoreLocatorMarker === 'undefined') {
        class PkStoreLocatorMarker {
            constructor(map, point, img = "", name = "", address = "", phone = "", link = "") {
                const imgContent = img ? `<img class="pk-store-locator__popup-img" src="${img}">` : "";
                const nameContent = name ? `<span class="pk-store-locator__popup-name">${name}</span>` : "";
                const addressContent = address ? `<span class="pk-store-locator__popup-address">${address}</span>` : "";
                const phoneContent = phone ? `<span class="pk-store-locator__popup-phone">${phone}</span>` : "";
                const linkContent = link ? `<a class="pk-store-locator__popup-link" href="${link.split(",")[0]}">${link.split(",")[1]}</a>` : "";

                this.icon = L.divIcon({
                    html: `
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
              <path 
                fill="currentColor" 
                d="M12.003 11.73q.668 0 1.14-.475q.472-.475.472-1.143t-.475-1.14q-.476-.472-1.143-.472t-1.14.476q-.472.475-.472 1.143t.475 1.14q.476.472 1.143.472M12 21.019q-3.525-3.117-5.31-5.814q-1.786-2.697-1.786-4.909q0-3.173 2.066-5.234Q9.037 3 12 3t5.03 2.062q2.066 2.061 2.066 5.234q0 2.212-1.785 4.909q-1.786 2.697-5.311 5.814"
              />
            </svg>`,
                    className: "pk-store-locator__marker-icon",
                    iconSize: [32, 32],
                });

                this.popup = L.popup({
                    autoClose: true,
                    closeOnEscapeKey: false,
                    closeOnClick: false,
                    closeButton: false,
                    maxWidth: 200,
                    className: "pk-store-locator__popup"
                }).setContent(imgContent + nameContent + addressContent + phoneContent + linkContent);

                this.marker = L.marker()
                    .setLatLng(point)
                    .setIcon(this.icon)
                    .bindPopup(this.popup)
                    .on('click', this.onClick.bind(this))
                    .addTo(map);
            }

            onClick() {
                const markerListDOM = Array.from(this.marker._map._container.closest('pk-store-locator-map').querySelectorAll("[data-marker]"));

                markerListDOM.forEach((item) => {
                    item.classList.remove("pk-store-locator__store--active");
                    const itemLat = parseFloat(item.dataset.lat);
                    const itemLng = parseFloat(item.dataset.lng);
                    if (itemLat === this.marker.getLatLng().lat && itemLng === this.marker.getLatLng().lng) {
                        item.classList.add("pk-store-locator__store--active");
                    }
                });
            }

            setActive() {
                this.marker.openPopup();
                this.popup.getElement().classList.add("pk-store-locator__store--active");
            }

            unsetActive() {
                this.marker.closePopup();
                this.popup.getElement().classList.remove("pk-store-locator__store--active");
            }
        }

        window.PkStoreLocatorMarker = PkStoreLocatorMarker;
    }
});